import { ArrowUpRight, Bell, ChevronRight, Menu, Moon, Plus, Search, Settings, Sun } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router'
import { Logo } from '@/components/brand/logo'
import { Skeleton } from '@/components/ui/skeleton'
import { useMe, useSignOut } from '@/features/auth/api'
import { useNotifications } from '@/features/notifications/api'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import { currentTheme, setTheme } from '@/lib/theme'
import { CommandPalette } from './command-palette'
import { CrumbContext, PaletteContext, useDashboard } from './shell'
import { navItemFor, visibleNav } from './nav'

const roleNames = { owner: 'Owner', admin: 'Admin', support: 'Support' } as const

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

/** Signed-in staff only: the grouped menu, the location bar and the page. */
export function Layout() {
  const me = useMe()
  const signOut = useSignOut()
  const location = useLocation()
  const dashboard = useDashboard()
  const notifications = useNotifications()
  const [crumb, setCrumb] = useState<string | null>(null)
  const [palette, setPalette] = useState(false)
  // The menu on small screens: open for the page it was opened on, so moving to another page closes it.
  const [sideOpenAt, setSideOpenAt] = useState<string | null>(null)
  const sideOpen = sideOpenAt === location.pathname
  const setSideOpen = (open: boolean) => setSideOpenAt(open ? location.pathname : null)
  const [theme, setThemeState] = useState(currentTheme)
  const openPalette = useCallback(() => setPalette(true), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement | null)?.tagName ?? '')
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPalette((open) => !open)
      } else if (e.key === '/' && !typing && !(e.target as HTMLElement | null)?.isContentEditable) {
        e.preventDefault()
        setPalette(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const here = navItemFor(location.pathname)
  useEffect(() => {
    document.title = `${crumb ?? here?.label ?? 'Control panel'} · Arva SEO control panel`
  }, [crumb, here])

  const badges = useMemo(() => {
    const d = dashboard.data
    const unread = notifications.data?.meta.unread ?? 0
    const out: Record<string, { n: number; tone: string } | null> = {
      '/tickets': d?.tickets.open ? { n: d.tickets.open, tone: d.tickets.unassigned > 0 ? 'red' : '' } : null,
      '/notifications': unread ? { n: unread, tone: '' } : null,
      '/subscriptions': d?.billing.past_due ? { n: d.billing.past_due, tone: 'amber' } : null,
    }
    return out
  }, [dashboard.data, notifications.data])

  if (me.isPending) return <Skeleton className="m-6 h-40 rounded-lg" />
  if (!me.data) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />

  const role = me.data.role
  const testMode = dashboard.data?.billing.test_mode
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setThemeState(next)
  }

  return (
    <CrumbContext.Provider value={setCrumb}>
      <PaletteContext.Provider value={openPalette}>
        <div className="app">
          <aside className={cn('side', sideOpen && 'open')} aria-label="Control panel">
            <div className="brand">
              <Link to="/" aria-label="Briefing">
                <Logo className="h-7" />
              </Link>
              <span className="cp-tag">Control</span>
            </div>
            <button type="button" className="kbar" onClick={openPalette}>
              <Search aria-hidden="true" />
              <span>Search or jump to…</span>
              <kbd>⌘K</kbd>
            </button>
            {visibleNav(role).map((group) => (
              <nav key={group.group} className="nav" aria-label={group.group}>
                <div className="nav-l">{group.group}</div>
                {group.items.map((item) => {
                  const Icon = item.icon
                  if (item.external) {
                    return (
                      <a
                        key={item.to}
                        href={`${env.apiOrigin}${item.to}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ni"
                      >
                        <Icon aria-hidden="true" />
                        <span>{item.label}</span>
                        <span className="meta">
                          <ArrowUpRight className="size-3.5" aria-label="opens in a new tab" />
                        </span>
                      </a>
                    )
                  }
                  const badge = badges[item.to]
                  const active = here?.to === item.to
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      className={cn('ni', active && 'on')}
                      aria-current={active ? 'page' : undefined}
                    >
                      <Icon aria-hidden="true" />
                      <span>{item.label}</span>
                      {badge ? (
                        <span className={cn('badge', badge.tone)}>
                          {badge.n > 99 ? '99+' : badge.n}
                          <span className="sr-only">
                            {item.to === '/notifications' ? ' unread' : ' waiting'}
                          </span>
                        </span>
                      ) : item.to === '/customers' && dashboard.data ? (
                        <span className="meta">{dashboard.data.customers.workspaces}</span>
                      ) : item.isNew ? (
                        <span className="new">New</span>
                      ) : null}
                    </NavLink>
                  )
                })}
              </nav>
            ))}
            <div className="side-foot">
              <div className="me">
                <span className="av av-me">{initials(me.data.name)}</span>
                <div className="min-w-0">
                  <b className="truncate">{me.data.name}</b>
                  <small className="truncate">{roleNames[role]}</small>
                </div>
                <Link to="/profile" className="iconbtn" aria-label="Your profile">
                  <Settings aria-hidden="true" />
                </Link>
              </div>
              <button
                type="button"
                onClick={() => signOut.mutate()}
                className="px-2.5 text-left text-xs text-sidebar-muted hover:text-white"
              >
                Sign out
              </button>
            </div>
          </aside>
          {sideOpen && (
            <div
              className="scrim"
              style={{ zIndex: 39 }}
              onClick={() => setSideOpen(false)}
              aria-hidden="true"
            />
          )}
          <div className="main">
            <header className="loc">
              <button
                type="button"
                className="iconbtn menu-toggle"
                onClick={() => setSideOpen(!sideOpen)}
                aria-label="Menu"
              >
                <Menu aria-hidden="true" />
              </button>
              <nav className="crumbs" aria-label="You are here">
                <Link to="/" className="hide-sm">
                  Control panel
                </Link>
                {here && here.to !== '/' && crumb && (
                  <>
                    <span className="sep">
                      <ChevronRight aria-hidden="true" />
                    </span>
                    <Link to={here.to}>{here.label}</Link>
                  </>
                )}
                <span className="sep">
                  <ChevronRight aria-hidden="true" />
                </span>
                <span className="here">{crumb ?? here?.label ?? 'Page'}</span>
              </nav>
              {testMode !== undefined && (
                <span
                  className={cn('envpill', testMode && 'test')}
                  title={
                    testMode
                      ? 'Lemon Squeezy is in test mode: no real payments'
                      : 'Lemon Squeezy is taking real payments'
                  }
                >
                  <span className="d" />
                  <span>{testMode ? 'Billing in test mode' : 'Billing live'}</span>
                </span>
              )}
              <button
                type="button"
                className="iconbtn"
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              >
                {theme === 'dark' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
              </button>
              <Link
                to="/notifications"
                className="iconbtn"
                aria-label={`Notifications${badges['/notifications'] ? `, ${badges['/notifications'].n} unread` : ''}`}
              >
                <Bell aria-hidden="true" />
                {badges['/notifications'] && <span className="dotn" />}
              </Link>
              <button type="button" className="btn sm primary" onClick={openPalette}>
                <Plus aria-hidden="true" />
                <span>New</span>
              </button>
            </header>
            <main className="page" id="page">
              <Outlet />
            </main>
          </div>
        </div>
        {palette && <CommandPalette onClose={() => setPalette(false)} role={role} />}
      </PaletteContext.Provider>
    </CrumbContext.Provider>
  )
}

/**
 * A page's header: the menu icon in a tile, the title in Fraunces, one line on what the page is
 * for, and the page's main actions on the right.
 */
export function PageTitle({
  title,
  description,
  actions,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
}) {
  const location = useLocation()
  const Icon = navItemFor(location.pathname)?.icon
  return (
    <header className="xh">
      {Icon && (
        <div className="xh-ic">
          <Icon aria-hidden="true" />
        </div>
      )}
      <div className="xh-t">
        <h1>{title}</h1>
        {description && <p className="xh-sub">{description}</p>}
      </div>
      {actions && <div className="acts">{actions}</div>}
    </header>
  )
}
